import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { CheckCircle2 } from "lucide-react";
import { publicApi, formatPublicError, honeypotOf } from "../lib/publicApi";
import { tr, LANG } from "../lib/i18n";

const E164 = /^\+[1-9]\d{7,14}$/;
const EMAIL = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;
const clean = (s) => (s || "").replace(/[\s\-().]/g, "");
const field = { width: "100%", padding: "10px 12px", border: "1px solid var(--pub-line, #d6d6d6)", borderRadius: 8, fontSize: 15, background: "#fff", color: "#111" };

const empty = { name: "", whatsapp: "+968", same: true, phone: "+968", email: "", message: "", preferred: "whatsapp", consent: false };

const Row = ({ label, children, error, id }) => (
  <label style={{ display: "block", marginBottom: 12, fontSize: 14, fontWeight: 600 }} htmlFor={id}>
    {label}
    <div style={{ marginTop: 4, fontWeight: 400 }}>{children}</div>
    {error ? <div role="alert" style={{ color: "#b42318", fontSize: 13, marginTop: 4 }} data-testid={`enq-err-${id}`}>{error}</div> : null}
  </label>
);

function validate(f) {
  const e = {};
  if (!f.name.trim()) e.name = tr("enq.err_name");
  if (!E164.test(clean(f.whatsapp))) e.whatsapp = tr("enq.err_phone");
  if (!f.same && !E164.test(clean(f.phone))) e.phone = tr("enq.err_phone");
  if (f.email && !EMAIL.test(f.email.trim())) e.email = tr("enq.err_email");
  if (!f.consent) e.consent = tr("enq.err_consent");
  return e;
}

export function EnquireDialog({ open, onClose, vehicle, branchId }) {
  const [f, setF] = useState(empty);
  const [errs, setErrs] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [fail, setFail] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const close = () => { onClose(); setTimeout(() => { setDone(null); setF(empty); setErrs({}); setFail(""); }, 300); };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate(f);
    setErrs(v);
    if (Object.keys(v).length) return;
    setSending(true);
    setFail("");
    try {
      const { data } = await publicApi.post("/enquiries", {
        enquire_v2: true, kind: vehicle ? "vehicle" : "contact", name: f.name.trim(), whatsapp: clean(f.whatsapp),
        phone: f.same ? clean(f.whatsapp) : clean(f.phone), email: f.email.trim() || null, message: f.message || null,
        preferred_contact: f.preferred, consent: f.consent, vehicle_id: vehicle?.id || vehicle?.stock_number || null,
        branch_id: branchId || null, lang: LANG, honeypot: honeypotOf(e),
      });
      setDone(data);
    } catch (err) {
      setFail(formatPublicError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="enquire-dialog" style={{ color: "#111", background: "#fff" }}>
        <DialogHeader className="pe-8">
          <DialogTitle>{vehicle ? tr("enq.title") : tr("enq.title_general")}</DialogTitle>
          <DialogDescription>{vehicle ? `${vehicle.stock_number} · ${vehicle.make} ${vehicle.model} ${vehicle.model_year || ""}` : tr("enq.general")}</DialogDescription>
        </DialogHeader>
        {done ? (
          <div style={{ textAlign: "center", padding: "20px 0" }} data-testid="enquire-thanks" role="status">
            <CheckCircle2 size={44} color="#15803d" style={{ margin: "0 auto 12px" }} />
            <p style={{ fontSize: 17, fontWeight: 600 }}>
              {done.salesperson ? tr("enq.thanks_named", { p0: done.salesperson }) : tr("enq.thanks")}
            </p>
            <button type="button" className="pub-cta dark" onClick={close} data-testid="enquire-close" style={{ marginTop: 16 }}>{tr("enq.close")}</button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate data-testid="enquire-form">
            <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: "absolute", insetInlineStart: 0, top: 0, width: 1, height: 1, opacity: 0, pointerEvents: "none" }} />
            <Row label={tr("enq.name")} error={errs.name} id="name">
              <input id="name" style={field} value={f.name} onChange={set("name")} autoComplete="name" required data-testid="enq-name" />
            </Row>
            <Row label={tr("enq.whatsapp")} error={errs.whatsapp} id="whatsapp">
              <input id="whatsapp" style={field} dir="ltr" inputMode="tel" value={f.whatsapp} onChange={set("whatsapp")} autoComplete="tel" data-testid="enq-whatsapp" />
            </Row>
            <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14, marginBottom: 12 }}>
              <input type="checkbox" checked={f.same} onChange={set("same")} data-testid="enq-same" /> {tr("enq.same")}
            </label>
            {!f.same && (
              <Row label={tr("enq.phone")} error={errs.phone} id="phone">
                <input id="phone" style={field} dir="ltr" inputMode="tel" value={f.phone} onChange={set("phone")} data-testid="enq-phone" />
              </Row>
            )}
            <Row label={tr("enq.email")} error={errs.email} id="email">
              <input id="email" type="email" style={field} dir="ltr" value={f.email} onChange={set("email")} autoComplete="email" data-testid="enq-email" />
            </Row>
            <Row label={tr("enq.message")} id="message">
              <textarea id="message" style={{ ...field, minHeight: 70 }} value={f.message} onChange={set("message")} data-testid="enq-message" />
            </Row>
            <fieldset style={{ border: 0, padding: 0, margin: "0 0 12px" }}>
              <legend style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{tr("enq.preferred")}</legend>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                {["whatsapp", "call", "email"].map((k) => (
                  <label key={k} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 14 }}>
                    <input type="radio" name="preferred" value={k} checked={f.preferred === k} onChange={set("preferred")} data-testid={`enq-pref-${k}`} />
                    {tr(`enq.pref_${k /* i18n-keys: whatsapp,call,email */}`)}
                  </label>
                ))}
              </div>
            </fieldset>
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 14, marginBottom: 4 }}>
              <input type="checkbox" checked={f.consent} onChange={set("consent")} style={{ marginTop: 3 }} data-testid="enq-consent" /> {tr("enq.consent")}
            </label>
            {errs.consent ? <div role="alert" style={{ color: "#b42318", fontSize: 13 }} data-testid="enq-err-consent">{errs.consent}</div> : null}
            {fail ? <div role="alert" style={{ color: "#b42318", fontSize: 14, marginTop: 8 }} data-testid="enq-fail">{fail}</div> : null}
            <button type="submit" className="pub-cta dark big full" disabled={sending} style={{ marginTop: 16, width: "100%" }} data-testid="enq-submit">
              {sending ? tr("enq.sending") : tr("enq.send")}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function PubWaSetter({ number, text }) {
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("pub-wa", { detail: { number, text } }));
    return () => { window.dispatchEvent(new CustomEvent("pub-wa", { detail: null })); };
  }, [number, text]);
  return null;
}

export function EnquireButton({ vehicle, branchId, className = "pub-cta", label }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={className} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        data-testid={vehicle ? `enquire-btn-${vehicle.stock_number}` : "enquire-btn-general"}>
        {label || tr("enq.button")}
      </button>
      <EnquireDialog open={open} onClose={() => setOpen(false)} vehicle={vehicle} branchId={branchId} />
    </>
  );
}
